<template>
    <div class="timeline-wrapper">
        <!-- Loading Overlay -->
        <TimelineLoadingOverlay v-if="isLoading" />
        
        <!-- Timeline Container -->
        <div
            ref="timeline"
            class="timeline"
            :style="{ height: timelineHeight, overflow: 'hidden' }"
        >
            <!-- No Data Overlay - positioned inside timeline to preserve header -->
            <TimelineEmptyState v-if="!isLoading && (!rowData || rowData.length === 0)" />
        </div>
        
        <!-- Pagination Controls - Always visible -->
        <PaginationControls
            :current-page="pagination.currentPage.value"
            :page-size="pagination.pageSize.value"
            :total-pages="pagination.totalPages.value"
            :total-items="groupByRef === 'TEAMS' ? timelineData.totalTeams.value : timelineData.totalUsers.value"
            :pagination-start="pagination.paginationStart.value"
            :pagination-end="pagination.paginationEnd.value"
            :page-size-options="[5, 10, 20]"
            @update:page-size="onPageSizeChange"
            @first-page="onFirstPage"
            @previous-page="pagination.goToPreviousPage"
            @next-page="pagination.goToNextPage"
            @last-page="pagination.goToLastPage"
            :item-label="groupByRef === 'TEAMS' ? 'Teams' : 'Users'"
        />
        
        <!-- Legend Footer -->
        <LegendFooter
            v-if="timelineInstance.legendItems.value.length > 0"
            :items="timelineInstance.legendItems.value"
            title="Workload:"
        />
    </div>
</template>

<script setup lang="ts">
import {
    ref, onBeforeUnmount, toRef, watch, computed, inject, type Ref
} from 'vue';
import type { TimeFormatPreference } from '@/utils/timeFormat';
import '@agyletime/agyle-vis-timeline/dist/vis-timeline-graph2d.min.css';
import TimelineLoadingOverlay from '../../molecules/timeline-overlays/TimelineLoadingOverlay.vue';
import TimelineEmptyState from '../../molecules/timeline-overlays/TimelineEmptyState.vue';
import PaginationControls from '@/components/Commons/UI/molecules/PaginationControls.vue';
import LegendFooter from '@/components/Commons/UI/molecules/LegendFooter.vue';
import { useTimelinePagination } from '../../../composables/useTimelinePagination';
import { useTimelineData } from '../../../composables/useTimelineData';
import { useTimelineOptions } from '../../../composables/useTimelineOptions';
import { useTimelineInstance } from '../../../composables/useTimelineInstance';
import { useVisLibraryLoader } from '../../../composables/useVisLibraryLoader';
import { useTimelineHeight } from '../../../composables/useTimelineHeight';
import { useTimelineWatchers, useTimelinePaginationWatcher } from '../../../composables/useTimelineWatchers';
import { useTimelineEventHandlers } from '../../../composables/useTimelineEventHandlers';
import { useTimelineZoomAPI } from '../../../composables/useTimelineZoomAPI';
import type { UserData } from '../../../utils/types';

interface Props {
    rowData: UserData[];
    totalTeamSchedule: number;
    columnDisplay: { userId?: boolean; email?: boolean};
    groupBy: string;
    isLoading: boolean;
    timezone?: string;
    startDate?: string;
    endDate?: string;
    hasViewLeaveTypePermission?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    rowData: () => [],
    totalTeamSchedule: 0,
    columnDisplay: () => ({ userId: true, email: true }),
    groupBy: 'NONE',
    isLoading: false,
    timezone: undefined,
    startDate: undefined,
    endDate: undefined,
    hasViewLeaveTypePermission: false
});

// Refs
const timeline = ref<HTMLElement | null>(null);

// Inject user time format preference (it's already a Ref from parent)
const userTimeFormatPreference = inject<Ref<TimeFormatPreference | null>>('userTimeFormatPreference', ref({ timeFormat: '24h' }));

// Convert props to refs for composables
const rowDataRef = toRef(props, 'rowData');
const totalTeamScheduleRef = toRef(props, 'totalTeamSchedule');
const groupByRef = toRef(props, 'groupBy');
const timezoneRef = toRef(props, 'timezone');
const startDateRef = toRef(props, 'startDate');
const endDateRef = toRef(props, 'endDate');
const hasViewLeaveTypePermissionRef = toRef(props, 'hasViewLeaveTypePermission');

// Compute date boundaries from props
const dateBoundaries = computed(() => {
    if (props.startDate && props.endDate) {
        return {
            start: new Date(props.startDate),
            end: new Date(props.endDate)
        };
    }
    return undefined;
});

// Use composables
const pagination = useTimelinePagination(() => groupByRef.value === 'TEAMS'
    ? timelineData.totalTeams.value : timelineData.totalUsers.value);
const timelineData = useTimelineData({
    rowData: rowDataRef,
    totalTeamSchedule: totalTeamScheduleRef,
    currentPage: pagination.currentPage,
    pageSize: pagination.pageSize,
    groupBy: groupByRef
});

const timelineOptions = useTimelineOptions(7, timezoneRef, userTimeFormatPreference);
const timelineInstance = useTimelineInstance(
    timeline,
    timelineOptions.options,
    timelineOptions.updateDateRange,
    timezoneRef,
    groupByRef,
    totalTeamScheduleRef,
    dateBoundaries,
    userTimeFormatPreference,
    startDateRef,
    endDateRef,
    hasViewLeaveTypePermissionRef
);

const { isLibraryLoaded, isLoaded } = useVisLibraryLoader();

// Calculate dynamic height - use filteredApiData to match timeline display logic
const { timelineHeight } = useTimelineHeight({
    filteredData: timelineData.filteredApiData,
    groupBy: groupByRef,
    currentPage: pagination.currentPage,
    pageSize: pagination.pageSize,
    collapsedGroups: timelineInstance.collapsedGroups
});

// Event handlers using composable
const { onPageSizeChange, onFirstPage } = useTimelineEventHandlers(
    timelineInstance,
    pagination,
    timelineData,
    props
);

// Setup watchers
useTimelineWatchers({
    props,
    timelineInstance,
    timelineData,
    isLibraryLoaded,
    pagination: { currentPage: pagination.currentPage, pageSize: pagination.pageSize }
});
useTimelinePaginationWatcher({
    currentPage: pagination.currentPage,
    pageSize: pagination.pageSize,
    timelineInstance,
    timelineData,
    props
});

// Watch for library loaded and initialize if we have data or need to show header
watch(isLoaded, (loaded) => {
    if (loaded && !timelineInstance.isInitialized()) {
        // Always initialize timeline to show header, even with empty data
        timelineInstance.initializeTimeline(
            timelineData.paginatedData.value,
            props.groupBy,
            props.columnDisplay
        );
        // Update header with initial count
        timelineInstance.updateGroupHeader(timelineData.totalUsers.value, groupByRef.value);
    }
});

// Watch for timezone changes and reinitialize
watch(() => props.timezone, () => {
    if (timelineInstance.isInitialized() && timelineData.paginatedData.value) {
        timelineInstance.reinitializeTimeline(
            timelineData.paginatedData.value,
            props.groupBy,
            props.columnDisplay
        );
        timelineInstance.updateGroupHeader(timelineData.totalUsers.value, groupByRef.value);
    }
});

// Watch for total users count changes and update header
watch(() => timelineData.totalUsers.value, (newTotal) => {
    if (timelineInstance.isInitialized()) {
        // Always show total user count in header, regardless of grouping mode
        timelineInstance.updateGroupHeader(newTotal, groupByRef.value);
    }
}, { immediate: true });

// Update header when groupBy changes
watch(groupByRef, () => {
    if (timelineInstance.isInitialized()) {
        timelineInstance.updateGroupHeader(timelineData.totalUsers.value, groupByRef.value);
    }
});

// Register zoom API with parent component
useTimelineZoomAPI(timelineInstance);

// Lifecycle
onBeforeUnmount(() => {
    timelineInstance.destroyTimeline();
});
</script>

<style scoped src="./TeamAndScheduleTimeline.css"></style>
